import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton
} from '@mui/material';
import { fetchFormType, fetchRegionList } from 'API/approverApi';
import { INITIAL_APPROVER_VALUES } from 'Helpers/constant';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { approverSchema } from 'Validation/approverSchema';
import { useApproverForm } from '../../../features/approver/useApproverForm';
import ApproverForm from './ApproverForm';
import './ManageApprover.scss';

const AddEditApprover = ({ onClose, isOpen = false, isEditing = false, editData = null }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { formTypeList } = useSelector((state) => state.approver);
  const { handleAdd, handleUpdate, resetForm } = useApproverForm(onClose);

  const methods = useForm({
    resolver: yupResolver(approverSchema(formTypeList)),
    mode: 'all',
    defaultValues: INITIAL_APPROVER_VALUES
  });

  const { handleSubmit, reset, setValue } = methods;

  useEffect(() => {
    dispatch(fetchFormType('Admin')).unwrap();
    dispatch(fetchRegionList()).unwrap();
  }, [dispatch]);

  useEffect(() => {
    if (isEditing && editData) {
      const regionIds = editData.regions?.map((region) => region.id) || [];
      reset({
        ...INITIAL_APPROVER_VALUES,
        ...editData
      });

      setValue('regions', regionIds);
      const approverEmails = editData?.approver_details || [];
      approverEmails.forEach((approver) => {
        switch (approver.approval_number) {
          case 1:
            setValue('approverEmail', approver.email);
            setValue('approverName', approver.name);
            break;
          case 2:
            setValue('secondApproverEmailAddress', approver.email);
            setValue('secondApprover', true);
            setValue('secondApproverName', approver.name);
            break;
          case 3:
            setValue('thirdApproverEmailAddress', approver.email);
            setValue('thirdApprover', true);
            setValue('thirdApproverName', approver.name);
            break;
        }
      });
    } else {
      reset(INITIAL_APPROVER_VALUES);
    }
  }, [isEditing, editData, reset, setValue]);

  const handlePopupClose = () => {
    resetForm(reset);
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (isEditing) {
        await handleUpdate(data);
      } else {
        await handleAdd(data);
      }
      handlePopupClose();
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handlePopupClose();
        }
      }}
      disableEscapeKeyDown={true}
      fullWidth
      maxWidth="lg"
      className="add-edit-approver">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle pb={0} className="dialog-title">
            <span className="dialog-title-text">
              {isEditing
                ? t('approverManagement.editApprover')
                : t('approverManagement.addApprover')}
            </span>
            <IconButton className="dialog-IconButton" aria-label="close" onClick={handlePopupClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider className="seperator" />

          <DialogContent className="dialog-content">
            <ApproverForm isEditing={isEditing} formTypeList={formTypeList} />
          </DialogContent>

          <Divider className="seperator seperator-bottom" />

          <DialogActions className="dialog-footer-btn-container">
            <Box className="modal-footer-btn-wrapper">
              <Button onClick={handlePopupClose} className="cancel-btn">
                {t('button.cancel')}
              </Button>

              <Button
                type="submit"
                className="submit-btn"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}>
                {isEditing
                  ? loading
                    ? t('button.loadingUpdate')
                    : t('button.update')
                  : loading
                    ? t('button.loadingSave')
                    : t('button.save')}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddEditApprover;
